import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Building2, CheckCircle, Clock, XCircle, Upload, AlertTriangle,
  FileText, ChevronRight, Send, Loader2, Info
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';
const BRAND_GREEN = '#22C55E';

const CompanyVerificationPortal = () => {
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const res = await api.get('/company-approval/my-verification');
      setVerificationData(res.data);
      
      // Set first incomplete step as active
      const firstIncomplete = res.data.steps?.find(
        s => s.status === 'pending' || s.status === 'denied'
      );
      if (firstIncomplete) {
        setActiveStep(firstIncomplete.id);
        // Pre-populate answers from existing data
        const existingAnswers = {};
        firstIncomplete.questions?.forEach(q => {
          if (q.answer !== undefined) {
            existingAnswers[q.id] = q.answer;
          }
        });
        setAnswers(existingAnswers);
      }
    } catch (error) {
      toast.error('Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleFileUpload = async (stepId, questionId, file) => {
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post(`/company-approval/steps/${stepId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAnswers(prev => ({ ...prev, [questionId]: res.data.file_url }));
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmitStep = async (stepId) => {
    // Validate required fields
    const step = verificationData.steps.find(s => s.id === stepId);
    const requiredQuestions = step.questions.filter(q => q.is_required && q.field_type !== 'note');
    
    for (const q of requiredQuestions) {
      if (!answers[q.id] || (typeof answers[q.id] === 'string' && !answers[q.id].trim())) {
        toast.error(`Please answer: "${q.question_text.replace(/<[^>]*>/g, '').substring(0, 50)}..."`);
        return;
      }
    }
    
    setSubmitting(true);
    try {
      await api.post(`/company-approval/steps/${stepId}/submit`, { answers });
      toast.success('Step submitted for review');
      fetchVerificationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit step');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'submitted':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'denied':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-700',
      submitted: 'bg-blue-100 text-blue-700',
      denied: 'bg-red-100 text-red-700',
      pending: 'bg-gray-100 text-gray-600'
    };
    const labels = {
      approved: 'Approved',
      submitted: 'Under Review',
      denied: 'Needs Revision',
      pending: 'Not Started'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || 'Pending'}
      </span>
    );
  };

  const renderQuestionField = (question, stepId) => {
    const value = answers[question.id] || '';
    
    switch (question.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder || ''}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder || ''}
            rows={4}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select an option...</option>
            {(question.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <div className="space-y-2">
            {(question.options || []).map((opt, i) => {
              const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
              const isChecked = currentValues.includes(opt);
              
              return (
                <label key={i} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const newValues = e.target.checked 
                        ? [...currentValues, opt]
                        : currentValues.filter(v => v !== opt);
                      handleAnswerChange(question.id, newValues);
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-gray-700">{opt}</span>
                </label>
              );
            })}
          </div>
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder || ''}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'file':
        return (
          <div className="space-y-2">
            {value && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>File uploaded</span>
                <a href={value} target="_blank" rel="noopener noreferrer" className="underline">View</a>
              </div>
            )}
            <label className="flex items-center gap-2 px-4 py-3 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50">
              <Upload className="w-5 h-5 text-gray-400" />
              <span className="text-gray-600">{uploadingFile ? 'Uploading...' : 'Click to upload file'}</span>
              <input
                type="file"
                className="hidden"
                disabled={uploadingFile}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleFileUpload(stepId, question.id, e.target.files[0]);
                  }
                }}
              />
            </label>
          </div>
        );
      
      case 'note':
        return (
          <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700" dangerouslySetInnerHTML={{ __html: question.question_text }} />
            </div>
          </div>
        );
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        );
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  // Check if company is fully verified
  const isVerified = verificationData?.verification_status === 'verified';
  const allStepsApproved = verificationData?.steps?.every(s => s.status === 'approved');
  const hasSteps = verificationData?.steps?.length > 0;

  if (isVerified || allStepsApproved) {
    return (
      <Sidebar>
        <div className="p-8 bg-gray-50/50 min-h-screen">
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">Company Verified!</h1>
            <p className="text-gray-600">
              Congratulations! Your company has been fully verified. You now have full access to all platform features.
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (!hasSteps) {
    return (
      <Sidebar>
        <div className="p-8 bg-gray-50/50 min-h-screen">
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-blue-500" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">Verification Coming Soon</h1>
            <p className="text-gray-600">
              The verification process is being set up by the platform administrator. Please check back later.
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Company Verification</h1>
                <p className="text-gray-500 text-sm">Complete the following steps to verify your company</p>
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Verification Progress</h2>
              <span className="text-sm text-gray-500">
                {verificationData.steps.filter(s => s.status === 'approved').length} of {verificationData.steps.length} steps complete
              </span>
            </div>
            <div className="flex gap-2">
              {verificationData.steps.map((step, index) => (
                <div 
                  key={step.id}
                  className={`flex-1 h-2 rounded-full ${
                    step.status === 'approved' ? 'bg-green-500' :
                    step.status === 'submitted' ? 'bg-blue-500' :
                    step.status === 'denied' ? 'bg-red-500' :
                    'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Steps List */}
          <div className="space-y-4">
            {verificationData.steps.map((step, index) => {
              const isActive = activeStep === step.id;
              const isDisabled = index > 0 && verificationData.steps[index - 1].status !== 'approved';
              const isComplete = step.status === 'approved';
              
              return (
                <div 
                  key={step.id} 
                  className={`bg-white rounded-xl shadow-sm border ${isActive ? 'border-blue-500' : 'border-gray-100'}`}
                  data-testid={`verification-step-${step.id}`}
                >
                  {/* Step Header */}
                  <button
                    onClick={() => {
                      if (!isDisabled) {
                        setActiveStep(isActive ? null : step.id);
                        // Load existing answers
                        const existingAnswers = {};
                        step.questions?.forEach(q => {
                          if (q.answer !== undefined) {
                            existingAnswers[q.id] = q.answer;
                          }
                        });
                        setAnswers(existingAnswers);
                      }
                    }}
                    disabled={isDisabled}
                    className={`w-full p-5 flex items-center justify-between ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                        isComplete ? 'bg-green-100 text-green-600' :
                        step.status === 'denied' ? 'bg-red-100 text-red-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {isComplete ? <CheckCircle className="w-5 h-5" /> : index + 1}
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">{step.name}</h3>
                        {step.description && (
                          <p className="text-sm text-gray-500">{step.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(step.status)}
                      <ChevronRight className={`w-5 h-5 text-gray-400 transition ${isActive ? 'rotate-90' : ''}`} />
                    </div>
                  </button>

                  {/* Admin Notes (if denied) */}
                  {step.status === 'denied' && step.admin_notes && (
                    <div className="mx-5 mb-4 p-4 bg-red-50 border border-red-100 rounded-xl">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-700 text-sm">Revision Required</p>
                          <p className="text-sm text-red-600 mt-1">{step.admin_notes}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step Questions */}
                  {isActive && step.status !== 'approved' && (
                    <div className="px-5 pb-5 border-t border-gray-100">
                      <div className="pt-5 space-y-6">
                        {step.questions?.map((question) => (
                          <div key={question.id} data-testid={`question-${question.id}`}>
                            {question.field_type !== 'note' && (
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <span dangerouslySetInnerHTML={{ __html: question.question_text }} />
                                {question.is_required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                            )}
                            {question.help_text && question.field_type !== 'note' && (
                              <p className="text-sm text-gray-500 mb-2">{question.help_text}</p>
                            )}
                            {renderQuestionField(question, step.id)}
                          </div>
                        ))}

                        {/* Submit Button */}
                        <div className="pt-4">
                          <button
                            onClick={() => handleSubmitStep(step.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-6 py-3 text-white rounded-xl font-medium disabled:opacity-50"
                            style={{ backgroundColor: BRAND_BLUE }}
                            data-testid={`submit-step-${step.id}`}
                          >
                            {submitting ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                Submit for Review
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Completed Step View */}
                  {isActive && step.status === 'approved' && (
                    <div className="px-5 pb-5 border-t border-gray-100">
                      <div className="pt-5 space-y-4">
                        {step.questions?.map((question) => (
                          <div key={question.id} className="text-sm">
                            <p className="font-medium text-gray-700" dangerouslySetInnerHTML={{ __html: question.question_text }} />
                            <p className="text-gray-600 mt-1">
                              {Array.isArray(question.answer) ? question.answer.join(', ') : question.answer || '-'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default CompanyVerificationPortal;
